/**
 * Chutt... - Mission Gauge
 * Circular SVG gauge (100px) for mission scores with subtle glow
 */
import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { colors } from '../../styles/colors';

function MissionGauge({ score = 0, size = 100, label }) {
  const progress = Math.min(100, Math.max(0, score));
  const radius = 38;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const getColor = (pct) => {
    if (pct >= 70) return colors.success;
    if (pct >= 40) return colors.warning;
    return colors.danger;
  };

  const color = getColor(progress);

  return (
    <Tooltip title={`Score mission : ${Math.round(progress)}%`}>
      <Box sx={{ textAlign: 'center', cursor: 'pointer' }}>
        <Box sx={{ position: 'relative', width: size, height: size, mx: 'auto' }}>
          <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label={`${label || 'Mission'}: ${Math.round(progress)}%`}>
            {/* Glow filter */}
            <defs>
              <filter id={`glow-${label || 'mission'}`} x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            {/* Background track */}
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={colors.gray200}
              strokeWidth="6"
            />
            {/* Progress arc */}
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 50 50)"
              filter={progress > 0 ? `url(#glow-${label || 'mission'})` : undefined}
              style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1)' }}
            />
          </svg>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: size >= 100 ? '1.1rem' : '0.85rem',
                fontWeight: 'bold',
                color: color,
                lineHeight: 1,
              }}
            >
              {Math.round(progress)}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '0.68rem',
                color: colors.gray500,
                lineHeight: 1,
                mt: 0.2,
              }}
            >
              / 100
            </Typography>
          </Box>
        </Box>
        {label && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              color: colors.gray500,
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.68rem',
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

export default MissionGauge;
